// @flow

import React, { forwardRef, useRef, useEffect, useState } from "react";
import { localize } from "redux-i18n";
import { type StepProps, SchemaForm, Button, View } from "@components";

type OwnProps = $Exact<{
  schema: Object,
  uiSchema: Object,
  schemaColumns?: number,
  filters: Object,
  prevStepDone: boolean,
  enableNext?: Object,
  getView?: (filters: Object) => React$Node,
  hideNextButton?: boolean,
  customErrors?: [],
  filtersAreValid: boolean,
}>;

type PropsFromState = $Exact<{
  values: Object,
}>;

type Props = $Exact<{
  ...PropsFromLocalize,
  ...PropsFromState,
  ...StepProps,
  ...OwnProps,
}>;

const StepFilters = forwardRef(function StepFilters(props, ref) {
  const formRef = useRef(null);
  const [formData, setFormData] = useState(null);
  const [isValid, setIsValid] = useState(false);
  const { schema, uiSchema, filters } = props;

  useEffect(() => {
    setIsValid(validate());
    setFormData(filters || {});
  }, [filters]);

  const readOnlyData =
    !props.editable && props.prevStepDone && props.filters && props.getView
      ? props.getView(props.filters)
      : null;

  function handleSubmit() {
    props.handleSubmit && props.handleSubmit({ filters: formData }, isValid);
  }

  function handleChange(formData: Object) {
    props.onChange && props.onChange({ filters: formData }, isValid);
  }
  function handleError(errors: Object[]) {
    props.onError && props.onError(errors);
  }

  function validate() {
    if (!formRef.current?.state) return true;
    const errors = formRef.current.state.errors;
    return errors.length === 0;
  }

  function replaceNullWithEmptyString(data: Object) {
    const filters = { ...data };
    if (!filters) return (props || {}).filters;

    for (let prop in filters) {
      if (filters[prop] === null) {
        filters[prop] = "";
      }
    }

    return filters;
  }

  if (!props.editable) {
    return null;
  }

  return props.editable && props.schema ? (
    <View flex={1}>
      {uiSchema && schema && formData && (
        <SchemaForm
          id={new Date().getTime()}
          className={`columns-${props.schemaColumns || 2}`}
          ref={formRef}
          formData={formData}
          liveValidate={true}
          hideLiveErrors={true}
          onSubmit={handleSubmit}
          onChange={handleChange}
          onError={handleError}
          schema={schema}
          uiSchema={uiSchema}
          externalSubmit
        />
      )}
      {props.customErrors && props.customErrors.length > 0 ? (
        <div className="alert alert-danger w-100 m-0">{props.customErrors.map(error => error)}</div>
      ) : null}
      {props.editable && props.enableNext && (
        <div className="d-flex justify-content-end align-items-center w-100 mt-2 mb-2 mr-4">
          <Button disabled={!isValid} onClick={handleSubmit}>
            {props.t("NEXT_SLIDES")}
          </Button>
        </div>
      )}
    </View>
  ) : (
    readOnlyData
  );
});

export default localize()(StepFilters);
